import { cva, type VariantProps } from "class-variance-authority";
import { type ComponentProps } from "react";

import { cn } from "@/lib/utils";

const alertVariants = cva(
  cn(
    "relative w-full rounded-[4px] border px-4 py-3 text-sm",
    `
      [&>svg]:absolute [&>svg]:top-4 [&>svg]:left-4
      [&>svg~*]:pl-7
    `,
    "[&>svg+div]:translate-y-[-3px]",
  ),
  {
    variants: {
      variant: {
        /** Same border and surface as console cards; label text colour. */
        default: cn(
          "border-(--console-border) bg-card text-(--console-text-label)",
          "[&>svg]:text-(--console-text-label)",
        ),
        destructive: cn(
          "border-destructive/50 text-destructive",
          `
            dark:border-destructive
            [&>svg]:text-destructive
          `,
        ),
        /** Same colours as the warning toast. */
        warning: cn(
          "border-(--feedback-warn-color) bg-(--feedback-warn-bg)",
          "text-(--feedback-warn-color)",
        ),
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

const Alert = ({
  className,
  variant,
  ref,
  ...props
}: ComponentProps<"div"> & VariantProps<typeof alertVariants>) => (
  <div
    ref={ref}
    role="alert"
    className={cn(alertVariants({ variant }), className)}
    {...props}
  />
);
Alert.displayName = "Alert";

const AlertDescription = ({
  className,
  ref,
  ...props
}: ComponentProps<"div">) => (
  <div
    ref={ref}
    className={cn(
      `
        text-sm
        [&_p]:leading-relaxed
      `,
      className,
    )}
    {...props}
  />
);
AlertDescription.displayName = "AlertDescription";

export { Alert, AlertDescription };
